<template><div><component-combinator component-name="DtNotice" /><h2 id="usage" tabindex="-1" class="d-docsite--header-2">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">A notice delivers informational and assistive messages that inform the user about product or account statuses and related actions. It can suggest an action or dismissed by the user, though neither are required. For time-based notifications, see <RouteLink to="/components/toast.html" class="d-docsite--link d-link">Toast</RouteLink>.</p>
<h2 id="variants-and-examples" tabindex="-1" class="d-docsite--header-2">Variants and Examples <a class="header-anchor d-link d-docsite--link d-link" href="#variants-and-examples" aria-hidden="true">#</a></h2>
<h3 id="base-styles" tabindex="-1" class="d-docsite--header-3">Base Styles <a class="header-anchor d-link d-docsite--link d-link" href="#base-styles" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Used in most scenarios when the message should be noticeable but not dominate.</p>
<code-example bgclass="d-bgc-primary" source-code='&lt;dt-notice
  title=&quot;Base title (optional)&quot;
&gt;
  &lt;span&gt;
    Message body with
    &lt;a
      href=&quot;#&quot;
      class=&quot;d-link&quot;
      :class=&quot;linkClass&quot;
    &gt;a link&lt;/a&gt;.
  &lt;/span&gt;
  &lt;template #action&gt;
    &lt;dt-button
      :size=&quot;200&quot;
      importance=&quot;outlined&quot;
      kind=&quot;muted&quot;
      @click=&quot;onClick&quot;
    &gt;
      Action
    &lt;/dt-button&gt;
  &lt;/template&gt;
&lt;/dt-notice&gt;
&lt;dt-notice
  title=&quot;Info title (optional)&quot;
  kind=&quot;info&quot;
&gt;
  &lt;span&gt;
    Message body with
    &lt;a
      href=&quot;#&quot;
      class=&quot;d-link&quot;
      :class=&quot;linkClass&quot;
    &gt;a link&lt;/a&gt;.
  &lt;/span&gt;
  &lt;template #action&gt;
    &lt;dt-button
      :size=&quot;200&quot;
      importance=&quot;outlined&quot;
      kind=&quot;muted&quot;
      @click=&quot;onClick&quot;
    &gt;
      Action
    &lt;/dt-button&gt;
  &lt;/template&gt;
&lt;/dt-notice&gt;
&lt;dt-notice
  title=&quot;Critical title (optional)&quot;
  kind=&quot;critical&quot;
&gt;
  &lt;span&gt;
    Message body with
    &lt;a
      href=&quot;#&quot;
      class=&quot;d-link&quot;
      :class=&quot;linkClass&quot;
    &gt;a link&lt;/a&gt;.
  &lt;/span&gt;
  &lt;template #action&gt;
    &lt;dt-button
      :size=&quot;200&quot;
      importance=&quot;outlined&quot;
      kind=&quot;muted&quot;
      @click=&quot;onClick&quot;
    &gt;
      Action
    &lt;/dt-button&gt;
  &lt;/template&gt;
&lt;/dt-notice&gt;
&lt;dt-notice
  title=&quot;Positive title (optional)&quot;
  kind=&quot;positive&quot;
&gt;
  &lt;span&gt;
    Message body with
    &lt;a
      href=&quot;#&quot;
      class=&quot;d-link&quot;
      :class=&quot;linkClass&quot;
    &gt;a link&lt;/a&gt;.
  &lt;/span&gt;
  &lt;template #action&gt;
    &lt;dt-button
      :size=&quot;200&quot;
      importance=&quot;outlined&quot;
      kind=&quot;muted&quot;
      @click=&quot;onClick&quot;
    &gt;
      Action
    &lt;/dt-button&gt;
  &lt;/template&gt;
&lt;/dt-notice&gt;
&lt;dt-notice
  title=&quot;Warning title (optional)&quot;
  kind=&quot;warning&quot;
&gt;
  &lt;span&gt;
    Message body with
    &lt;a
      href=&quot;#&quot;
      class=&quot;d-link&quot;
      :class=&quot;linkClass&quot;
    &gt;a link&lt;/a&gt;.
  &lt;/span&gt;
  &lt;template #action&gt;
    &lt;dt-button
      :size=&quot;200&quot;
      importance=&quot;outlined&quot;
      kind=&quot;muted&quot;
      @click=&quot;onClick&quot;
    &gt;
      Action
    &lt;/dt-button&gt;
  &lt;/template&gt;
&lt;/dt-notice&gt;'>
<dt-stack gap="100">
  <example-notice kind="base" title="Base title (optional)" />
  <example-notice kind="critical" title="Critical title (optional)" />
  <example-notice kind="info" title="Info title (optional)" />
  <example-notice kind="positive" title="Positive title (optional)" />
  <example-notice kind="warning" title="Warning title (optional)" />
</dt-stack>
</code-example>
<h3 id="important" tabindex="-1" class="d-docsite--header-3">Important <a class="header-anchor d-link d-docsite--link d-link" href="#important" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Used occasionally in scenarios when the message needs to dominate.</p>
<code-example source-code='&lt;dt-notice
  title=&quot;Base title (optional)&quot;
  important
&gt;
  &lt;span&gt;
    Message body with
    &lt;a
      href=&quot;#&quot;
      class=&quot;d-link&quot;
      :class=&quot;linkClass&quot;
    &gt;a link&lt;/a&gt;.
  &lt;/span&gt;
  &lt;template #action&gt;
    &lt;dt-button
      :size=&quot;200&quot;
      importance=&quot;outlined&quot;
      kind=&quot;inverted&quot;
      @click=&quot;onClick&quot;
    &gt;
      Action
    &lt;/dt-button&gt;
  &lt;/template&gt;
&lt;/dt-notice&gt;
&lt;dt-notice
  title=&quot;Info title (optional)&quot;
  kind=&quot;info&quot;
  important
&gt;
  &lt;span&gt;
    Message body with
    &lt;a
      href=&quot;#&quot;
      class=&quot;d-link&quot;
      :class=&quot;linkClass&quot;
    &gt;a link&lt;/a&gt;.
  &lt;/span&gt;
  &lt;template #action&gt;
    &lt;dt-button
      :size=&quot;200&quot;
      importance=&quot;outlined&quot;
      kind=&quot;inverted&quot;
      @click=&quot;onClick&quot;
    &gt;
      Action
    &lt;/dt-button&gt;
  &lt;/template&gt;
&lt;/dt-notice&gt;
&lt;dt-notice
  title=&quot;Critical title (optional)&quot;
  kind=&quot;critical&quot;
  important
&gt;
  &lt;span&gt;
    Message body with
    &lt;a
      href=&quot;#&quot;
      class=&quot;d-link&quot;
      :class=&quot;linkClass&quot;
    &gt;a link&lt;/a&gt;.
  &lt;/span&gt;
  &lt;template #action&gt;
    &lt;dt-button
      :size=&quot;200&quot;
      importance=&quot;outlined&quot;
      kind=&quot;inverted&quot;
      @click=&quot;onClick&quot;
    &gt;
      Action
    &lt;/dt-button&gt;
  &lt;/template&gt;
&lt;/dt-notice&gt;
&lt;dt-notice
  title=&quot;Positive title (optional)&quot;
  kind=&quot;positive&quot;
  important
&gt;
  &lt;span&gt;
    Message body with
    &lt;a
      href=&quot;#&quot;
      class=&quot;d-link&quot;
      :class=&quot;linkClass&quot;
    &gt;a link&lt;/a&gt;.
  &lt;/span&gt;
  &lt;template #action&gt;
    &lt;dt-button
      :size=&quot;200&quot;
      importance=&quot;outlined&quot;
      kind=&quot;inverted&quot;
      @click=&quot;onClick&quot;
    &gt;
      Action
    &lt;/dt-button&gt;
  &lt;/template&gt;
&lt;/dt-notice&gt;
&lt;dt-notice
  title=&quot;Warning title (optional)&quot;
  kind=&quot;warning&quot;
  important
&gt;
  &lt;span&gt;
    Message body with
    &lt;a
      href=&quot;#&quot;
      class=&quot;d-link&quot;
      :class=&quot;linkClass&quot;
    &gt;a link&lt;/a&gt;.
  &lt;/span&gt;
  &lt;template #action&gt;
    &lt;dt-button
      :size=&quot;200&quot;
      importance=&quot;outlined&quot;
      kind=&quot;inverted&quot;
      @click=&quot;onClick&quot;
    &gt;
      Action
    &lt;/dt-button&gt;
  &lt;/template&gt;
&lt;/dt-notice&gt;'>
<dt-stack gap="100">
  <example-notice important kind="base" title="Base title (optional)" />
  <example-notice important kind="critical" title="Critical title (optional)" />
  <example-notice important kind="info" title="Info title (optional)" />
  <example-notice important kind="positive" title="Positive title (optional)" />
  <example-notice important kind="warning" title="Warning title (optional)" />
</dt-stack>
</code-example>
<h3 id="truncate-text" tabindex="-1" class="d-docsite--header-3">Truncate Text <a class="header-anchor d-link d-docsite--link d-link" href="#truncate-text" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Truncates the text instead of wrapping it. Useful when the Notice needs to have a fixed height.</p>
<code-example bgclass="d-bgc-primary" source-code='&lt;dt-notice
  :truncate-text=&quot;true&quot;
  title=&quot;Lorem ipsum dolor sit amet, consectetur adipiscing elit,
    sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.&quot;
&gt;
  &lt;span&gt;
    Duis aute irure dolor in reprehenderit in voluptate velit
    esse cillum dolore eu fugiat nulla pariatur.
  &lt;/span&gt;
&lt;/dt-notice&gt;'>
<dt-notice
  :truncate-text="true"
  title="Lorem ipsum dolor sit amet, consectetur adipiscing elit,
    sed do eiusmod tempor incididunt ut labore et dolore magna aliqua."
>
  <span>
    Duis aute irure dolor in reprehenderit in voluptate velit
    esse cillum dolore eu fugiat nulla pariatur.
  </span>
</dt-notice>
</code-example>
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="notice" /><h2 id="classes" tabindex="-1" class="d-docsite--header-2">Classes <a class="header-anchor d-link d-docsite--link d-link" href="#classes" aria-hidden="true">#</a></h2>
<component-class-table component-name="notice" /><h2 id="accessibility" tabindex="-1" class="d-docsite--header-2">Accessibility <a class="header-anchor d-link d-docsite--link d-link" href="#accessibility" aria-hidden="true">#</a></h2>
<component-accessible-table component-name="notice" /></div></template>


<script setup>
  import ExampleNotice from '@exampleComponents/ExampleNotice.vue';
</script>